/**
 * Choosing whose plan this is.
 *
 * The planner is always scoped to one investor — `portfolio_planner/bootstrap/`
 * is keyed by user uuid and reads their KYC profile for the prefill — so a
 * planner opened from the sidebar has to ask first.
 *
 * Two lists are merged, both from endpoints the panel already uses:
 *
 *   /mutual_fund/investor_profile/list/  the MF investor profiles, which is who
 *                                        the planner can actually place for
 *   /user/user_list/                     every user, so a KYC-verified investor
 *                                        without an MF profile can still be
 *                                        planned for (nothing is placed until
 *                                        their MF onboarding is finished)
 *
 * KYC-verified investors sort first and the list defaults to them, because
 * those are the ones whose plan can be acted on.
 *
 * An admin without `can_see_all` sees only their own leads, as on the Users
 * and RFQ Orders pages — see `isVisibleLead`.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { makeGETRequestOnUserService } from "../../../Api/api";
import { canSeeAllLeads, isVisibleLead } from "../../../Helpers/adminPermissions";
import { getMFErrorMessage } from "../../../Api/mfAdminInvestments";
import type { IUser } from "../../../components/YieldCalculator/Interfaces";
import type { MFInvestorProfileRecord } from "../InvestorProfile";
import { InlineLoader } from "./PlannerFields";
import styles from "./portfolio-planner.module.css";

export interface PlannerInvestorOption {
  uuid: string;
  name: string;
  email: string;
  mobile: string;
  smestId: string;
  kycVerified: boolean;
  hasMfProfile: boolean;
  /** Whose lead this person is; decides whether a scoped admin sees them. */
  leadOwner: string;
}

const fullName = (first?: string | null, last?: string | null) =>
  [first, last].filter(Boolean).join(" ").trim();

const PlannerUserPicker = ({
  onSelect,
  onPlanForProspect,
}: {
  onSelect: (investor: PlannerInvestorOption) => void;
  onPlanForProspect: () => void;
}) => {
  const [investors, setInvestors] = useState<PlannerInvestorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [search, setSearch] = useState("");
  const [kycOnly, setKycOnly] = useState(true);
  const [mfOnly, setMfOnly] = useState(false);
  const leadsOnly = useMemo(() => !canSeeAllLeads(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    setWarning("");
    const merged: Record<string, PlannerInvestorOption> = {};
    const problems: string[] = [];

    const [profiles, users] = await Promise.all([
      makeGETRequestOnUserService("/mutual_fund/investor_profile/list/").catch(
        (requestError) => {
          problems.push(`MF investor profiles: ${getMFErrorMessage(requestError)}`);
          return null;
        }
      ),
      makeGETRequestOnUserService("/user/user_list/").catch((requestError) => {
        problems.push(`User directory: ${getMFErrorMessage(requestError)}`);
        return null;
      }),
    ]);

    const profileRows: MFInvestorProfileRecord[] = Array.isArray(
      (profiles as any)?.data?.data
    )
      ? (profiles as any).data.data
      : [];
    profileRows.forEach((profile) => {
      if (!profile.user) return;
      merged[profile.user] = {
        uuid: profile.user,
        name:
          fullName(profile.user_details?.firstName, profile.user_details?.lastName) ||
          "MF investor",
        email: profile.user_details?.emailId || "",
        mobile: profile.user_details?.mobileNumber || "",
        smestId: profile.user_details?.smestID || "",
        // The MF profile payload does not carry the KYC verification flag; the
        // user directory below fills it in where both lists have the person.
        kycVerified: false,
        hasMfProfile: profile.is_mf_investor_profile === true,
        leadOwner: profile.lead_owner || "",
      };
    });

    const userRows: IUser[] = Array.isArray((users as any)?.data)
      ? ((users as any).data as IUser[])
      : [];
    userRows.forEach((user) => {
      if (!user.uuid) return;
      const existing = merged[user.uuid];
      merged[user.uuid] = {
        uuid: user.uuid,
        name:
          fullName(user.firstName, user.lastName) ||
          (existing ? existing.name : "SMEST user"),
        email: user.emailId || (existing ? existing.email : ""),
        mobile: user.mobileNumber || (existing ? existing.mobile : ""),
        smestId: user.smestID || (existing ? existing.smestId : ""),
        kycVerified: user.areKYCDetailsVerified === true,
        hasMfProfile: existing ? existing.hasMfProfile : false,
        // The directory's owner is the one the Users page filters on; the MF
        // profile's copy only fills in when the directory has none.
        leadOwner: user.leadowner || (existing ? existing.leadOwner : ""),
      };
    });

    const list = Object.keys(merged)
      .map((uuid) => merged[uuid])
      .filter((investor) => isVisibleLead(investor.leadOwner));
    if (!list.length && problems.length) {
      setError(problems.join(" · "));
    } else if (problems.length) {
      setWarning(problems.join(" · "));
    }
    list.sort((left, right) => {
      if (left.hasMfProfile !== right.hasMfProfile) return left.hasMfProfile ? -1 : 1;
      if (left.kycVerified !== right.kycVerified) return left.kycVerified ? -1 : 1;
      return left.name.localeCompare(right.name);
    });
    setInvestors(list);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return investors
      .filter((investor) => (kycOnly ? investor.kycVerified : true))
      .filter((investor) => (mfOnly ? investor.hasMfProfile : true))
      .filter((investor) =>
        needle
          ? `${investor.name} ${investor.email} ${investor.mobile} ${investor.smestId}`
              .toLowerCase()
              .indexOf(needle) >= 0
          : true
      )
      .slice(0, 120);
  }, [investors, search, kycOnly, mfOnly]);

  return (
    <div className={styles.pickerCard}>
      {/* The badge labels this card rather than floating alone above it — on
          the investor-scoped view it sits in the topbar, where there is a row
          of controls for it to belong to. */}
      <span className={styles.adminBadge}>ADMIN · MF PORTFOLIO PLANNER</span>
      <h2>Whose plan is this?</h2>
      <p>
        The planner reads the selected investor&apos;s KYC profile for its
        prefill and prices the plan against live fund data, so it has to be
        opened for one person. KYC-verified investors are shown first — they are
        the ones whose recommended orders can actually be placed.
      </p>

      <div className={styles.searchWrap}>
        <SearchIcon className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          value={search}
          placeholder="Search by name, email, mobile or SMEST ID"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className={styles.pickerFilters}>
        <label>
          <input
            type="checkbox"
            checked={kycOnly}
            onChange={(event) => setKycOnly(event.target.checked)}
          />
          KYC-verified only
        </label>
        <label>
          <input
            type="checkbox"
            checked={mfOnly}
            onChange={(event) => setMfOnly(event.target.checked)}
          />
          Has an MF investor profile
        </label>
        <button
          type="button"
          className={styles.ghost}
          disabled={loading}
          aria-busy={loading || undefined}
          onClick={load}
        >
          {loading ? <InlineLoader /> : <RefreshIcon style={{ fontSize: 16 }} />}
          Reload
        </button>
        {leadsOnly ? (
          <span className={styles.scopeNote}>Showing your leads only</span>
        ) : null}
      </div>

      {warning ? <div className={styles.noticeWarn}>{warning}</div> : null}
      {error ? (
        <div className={styles.noticeBad}>
          <span>
            {error}
            <span className={styles.noticeActions}>
              <button type="button" className={styles.ghost} onClick={load}>
                Try again
              </button>
            </span>
          </span>
        </div>
      ) : null}

      {loading ? (
        <div className={styles.userList}>
          <div className={styles.empty}>
            <div className={styles.spinnerLarge} />
            Loading investors…
          </div>
        </div>
      ) : filtered.length ? (
        <div className={styles.userList}>
          {filtered.map((investor) => (
            <button
              type="button"
              key={investor.uuid}
              className={styles.userRow}
              onClick={() => onSelect(investor)}
            >
              <span className={styles.userRowIdentity}>
                <strong>{investor.name}</strong>
                <small>
                  {[investor.email, investor.mobile && `+91 ${investor.mobile}`, investor.smestId]
                    .filter(Boolean)
                    .join(" · ") || investor.uuid}
                </small>
              </span>
              <span className={styles.userTags}>
                {/*
                  The action belongs to the row it acts on, so it appears on the
                  row — on hover, and on keyboard focus. It sits to the LEFT of
                  the chips so the two chips stay flush against the right edge:
                  the CTA reserves its width even while invisible, and putting
                  it last pushed the chips inward and left a dead gap there.
                */}
                <span className={styles.userRowCta} aria-hidden="true">
                  Open planner
                  <ArrowForwardIcon style={{ fontSize: 15 }} />
                </span>
                <span className={investor.kycVerified ? styles.tagGood : styles.tagWarn}>
                  {investor.kycVerified ? "KYC verified" : "KYC pending"}
                </span>
                <span className={investor.hasMfProfile ? styles.tagGood : styles.tagNeutral}>
                  {investor.hasMfProfile ? "MF profile" : "No MF profile"}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <div className={styles.userList}>
          <div className={styles.empty}>
            {leadsOnly && !investors.length
              ? "No users are assigned to you as leads yet."
              : "No investor matches this search and these filters."}
          </div>
        </div>
      )}

      <div className={styles.pickerDivider}>or</div>

      <div className={styles.pickerAlt}>
        <div>
          <h3>Planning for someone who isn&apos;t a SMEST user yet</h3>
          <p>
            The plan and its report need no account — only the details entered
            in the planner. Orders cannot be placed until they sign up and
            finish MF onboarding.
          </p>
        </div>
        <button type="button" className={styles.tonal} onClick={onPlanForProspect}>
          <PersonAddAltIcon style={{ fontSize: 17 }} aria-hidden="true" />
          Plan for a prospect
        </button>
      </div>

    </div>
  );
};

export default PlannerUserPicker;
